import type { ReactNode } from 'react';
import type React from 'react';
import { createContext, useContext, useRef } from 'react';
import type { RoomType } from './RoomRegistrar';
import type { CommentBody } from '@liveblocks/client';

interface RoomOperations {
  createThread: (commentBody: CommentBody, blockId: string | null) => Promise<{ id: string }>;
  addComment: (threadId: string, body: CommentBody) => Promise<{ id: string }>;
  editComment: (threadId: string, commentId: string, body: CommentBody) => Promise<void>;
  deleteComment: (threadId: string, commentId: string) => Promise<void>;
  resolveThread: (threadId: string) => Promise<void>;
  unResolveThread: (threadId: string) => Promise<void>;
  deleteThread: (threadId: string) => Promise<void>;
}

interface GlobalRoomOperationsManager {
  registerRoomOperations: (roomType: RoomType, operations: RoomOperations) => void;
  executeInRoom: <T extends keyof RoomOperations>(
    roomType: RoomType,
    operation: T,
    ...args: Parameters<RoomOperations[T]>
  ) => Promise<ReturnType<RoomOperations[T]>>;
  canExecuteInRoom: (roomType: RoomType) => boolean;
}

const GlobalRoomOperationsContext = createContext<GlobalRoomOperationsManager | null>(null);

interface GlobalRoomOperationsProviderProps {
  children: ReactNode;
}

export const GlobalRoomOperationsProvider: React.FC<GlobalRoomOperationsProviderProps> = ({ children }) => {
  const roomOperationsRef = useRef<{
    [RoomType.PRIVATE]?: RoomOperations;
    [RoomType.PUBLIC]?: RoomOperations;
  }>({});

  const registerRoomOperations = (roomType: RoomType, operations: RoomOperations) => {
    roomOperationsRef.current[roomType] = operations;
  };

  const canExecuteInRoom = (roomType: RoomType) => {
    return !!roomOperationsRef.current[roomType];
  };

  const executeInRoom = async <T extends keyof RoomOperations>(
    roomType: RoomType,
    operation: T,
    ...args: Parameters<RoomOperations[T]>
  ): Promise<ReturnType<RoomOperations[T]>> => {
    const roomOps = roomOperationsRef.current[roomType];
    if (!roomOps) {
      throw new Error(`No operations available for ${roomType} room`);
    }

    const operationFn = roomOps[operation];
    if (!operationFn) {
      throw new Error(`Operation ${operation} not available for ${roomType} room`);
    }

    return (operationFn as (...args: unknown[]) => ReturnType<RoomOperations[T]>)(...args);
  };

  const manager: GlobalRoomOperationsManager = {
    registerRoomOperations,
    executeInRoom,
    canExecuteInRoom,
  };

  return <GlobalRoomOperationsContext.Provider value={manager}>{children}</GlobalRoomOperationsContext.Provider>;
};

export const useGlobalRoomOperations = (): GlobalRoomOperationsManager => {
  const context = useContext(GlobalRoomOperationsContext);
  if (!context) {
    throw new Error('useGlobalRoomOperations must be used within a GlobalRoomOperationsProvider');
  }
  return context;
};
